/**
 * French elapsed-time prompts and chrome (ADR 0051 part 3).
 *
 * The sentence templates use the impersonal "cela" ("that") rather than a
 * direct-object pronoun before the verb ("cela s'est terminé", not "elle
 * s'est terminée") - French past-participle agreement with a preceding
 * direct object would otherwise require knowing each activity's gender.
 * "Cela" sidesteps it by construction, the same way Spanish's templates
 * avoid the object pronoun entirely.
 */
export const timeSentences = {
    duration: (name: string, activity: string, start: string, end: string) =>
        `${name} a commencé ${activity} à ${start}. Cela s'est terminé à ${end}. Combien de temps cela a-t-il duré ?`,
    end: (name: string, activity: string, start: string, durationText: string) =>
        `${name} a commencé ${activity} à ${start} et cela a duré ${durationText}. À quelle heure cela s'est-il terminé ?`,
    start: (name: string, activity: string, end: string, durationText: string) =>
        `${name} a terminé ${activity} à ${end}, après ${durationText}. À quelle heure cela a-t-il commencé ?`,
};

/** name/activity pairs; each activity carries its own article (la lecture, l'entraînement, …). */
export const sentenceSubjects: [string, string][] = [
    ['Léa', 'la lecture'],
    ['Hugo', "l'entraînement de foot"],
    ['Chloé', 'le cours de piano'],
    ['Nathan', 'le cours de dessin'],
    ['Manon', 'la balade à vélo'],
    ['Louis', 'le cours de natation'],
    ['Camille', 'la promenade'],
    ['Adam', 'le projet de sciences'],
];

export const numberLinePrompts: Record<'duration' | 'end' | 'start', string> = {
    duration: 'Combien de temps s\'est écoulé ?',
    end: 'À quelle heure cela s\'est-il terminé ?',
    start: 'À quelle heure cela a-t-il commencé ?',
};

export const timeChrome = {
    digitalPrompt: 'Combien de temps s\'est écoulé ? ____________',
    afterWord: 'après',
    answerLabel: 'Réponse :',
};

export function durationInWords(totalMinutes: number): string {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const hourPart = hours > 0 ? `${hours} heure${hours === 1 ? '' : 's'}` : '';
    const minutePart = minutes > 0 ? `${minutes} minute${minutes === 1 ? '' : 's'}` : '';
    if (hourPart && minutePart) {
        return `${hourPart} ${minutePart}`;
    }
    return hourPart || minutePart || '0 minute';
}

/**
 * France reads the clock on 24 hours and has no a.m./p.m. convention, and
 * "du matin" / "de l'après-midi" breaks at 8 p.m. ("du soir"). "avant midi" /
 * "après midi" is the literal sense of ante/post meridiem and is right for
 * every time on both sides (ADR 0090 §8).
 */
export const dayHalves = { am: 'avant midi', pm: 'après midi', noon: 'midi' };

/** The A.M.-or-P.M. event bank; same events and windows as English (ADR 0090 §2). */
export const amPmEvents: { text: string; half: 'am' | 'pm'; from: number; to: number }[] = [
    { text: 'Léa prend son petit-déjeuner', half: 'am', from: 6 * 60, to: 8 * 60 + 30 },
    { text: "Hugo se réveille pour aller à l'école", half: 'am', from: 6 * 60, to: 7 * 60 + 30 },
    { text: 'Le bus scolaire passe prendre Chloé', half: 'am', from: 7 * 60, to: 8 * 60 + 30 },
    { text: "L'école commence pour Nathan", half: 'am', from: 8 * 60, to: 9 * 60 },
    { text: 'Le soleil se lève et le coq chante', half: 'am', from: 5 * 60, to: 7 * 60 },
    { text: "Manon promène le chien avant l'école", half: 'am', from: 6 * 60 + 30, to: 8 * 60 },
    { text: 'Louis a un cours de maths avant le déjeuner', half: 'am', from: 9 * 60, to: 10 * 60 + 30 },
    { text: 'Camille prend le repas du soir', half: 'pm', from: 17 * 60, to: 19 * 60 },
    { text: 'Adam va se coucher', half: 'pm', from: 19 * 60, to: 21 * 60 },
    { text: "Les étoiles apparaissent au-dessus de la maison d'Hugo", half: 'pm', from: 20 * 60, to: 22 * 60 },
    { text: "Léa prend son goûter après l'école", half: 'pm', from: 15 * 60, to: 16 * 60 + 30 },
    { text: "L'école finit pour Chloé", half: 'pm', from: 14 * 60 + 30, to: 15 * 60 + 30 },
    { text: 'Nathan lit une histoire avant de dormir', half: 'pm', from: 19 * 60, to: 20 * 60 + 30 },
    { text: "Manon a son entraînement de foot après l'école", half: 'pm', from: 15 * 60 + 30, to: 17 * 60 + 30 },
];

/** "Léa prend son petit-déjeuner à 7:30." */
export function amPmSentence(event: string, time: string): string {
    return `${event} à ${time}.`;
}
